import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import { getUnknownWhereaboutsReport } from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BasicUserInfo, UnknownWhereaboutsUserInfo } from "@shared/models/user.models";
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { TableContents } from '@shared/ui/atoms/TableStructure';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canUserViewTeamReports } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { failureNotification } from "@shared/store/notification.duck";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { sendWhereaboutsReminder } from '@features/whereabouts/services/Movements';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { useTranslation } from "react-i18next";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { BiggerThanTabletOnly, MobileOnly } from '@shared/ui/atoms/DeviceSpecific';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';


export function UnknownWhereaboutsReportingPage() {
  const [date, setDate] = useState<Moment>(moment());
  const [reportData, setReportData] = useState<UnknownWhereaboutsUserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const hasAccess = useMemo(() => canUserViewTeamReports(currentUser),[currentUser]);
  const {t} = useTranslation();

  const onDateChange = (date: Moment) => {
    setDate(date);
  }

  // showLoading=false performs a silent refresh (e.g. after sending a reminder) so the
  // table isn't replaced by the page spinner; the row keeps its own inline spinner.
  const loadReport = useCallback((showLoading: boolean): Promise<void> => {
    if (!date) {
      return Promise.resolve();
    }
    if (showLoading) {
      setReportData([]);
      setLoading(true);
    }
    return getUnknownWhereaboutsReport(date)
      .then(setReportData)
      .finally(() => {
        if (showLoading) {
          setLoading(false);
        }
      });
  }, [date]);

  useEffect(() => {
    loadReport(true);
  }, [loadReport]);

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      <HeadlineLarge>{t('reports-page.unknown-whereabouts.title')}</HeadlineLarge>
      <AccessRequired visible={!hasAccess} />
      {hasAccess &&
        <>
          {loading ? <>
            <Center>
              <LoadingSpinner hideBorder={true} />
            </Center>
          </> : <>
            <SpaceBetweenRow>
              <DateFlicker dateScale={DateScale.Week}
                           updateGlobalDate={false}
                           date={date}
                           onChange={onDateChange}
                           disabled={loading} />
              <div>
              </div>
            </SpaceBetweenRow>
            <ReportContainer>
              {reportData.length === 0 ? <>
                <FlatContentCard>
                  <BodyLarge weight={600}>{t('reports-page.unknown-whereabouts.note')}</BodyLarge>
                  <BodyRegular>{t('reports-page.unknown-whereabouts.all-users-have-whereabouts-set')}</BodyRegular>
                </FlatContentCard>
              </> : <>
                <table>
                  <tbody>
                  {reportData.map((user: UnknownWhereaboutsUserInfo, key: number) => (
                    <tr key={key}>
                      <UnknownWhereaboutsRow user={user} date={date} onReminderSent={() => loadReport(false)} />
                    </tr>
                  ))}
                  </tbody>
                </table>
              </>}
            </ReportContainer>
          </>}
        </>
      }
    </ReportingContainer>
  );
}

interface UnknownWhereaboutsRowProps {
  user: UnknownWhereaboutsUserInfo;
  date: Moment;
  onReminderSent: () => Promise<void>;
}

// Whole calendar days since the reminder was last sent. The backend stores last_updated as a
// date-only value and gates re-sends on calendar days, so we diff on day boundaries (rather than
// 24h windows) to keep the displayed count and the disabled state consistent with the server.
function daysSinceLastNotified(lastNotifiedDate?: Moment): number | undefined {
  return lastNotifiedDate
    ? moment().startOf('day').diff(moment(lastNotifiedDate).startOf('day'), 'days')
    : undefined;
}

function UnknownWhereaboutsRow(props: UnknownWhereaboutsRowProps) {
  const {user, date, onReminderSent} = props;
  const [lastNotifiedDate, setLastNotifiedDate] = useState<Moment | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  useEffect(() => {
    setLastNotifiedDate(!!user?.lastNotifiedDate ? moment(user.lastNotifiedDate) : undefined);
  }, [user]);

  const daysSinceReminder = daysSinceLastNotified(lastNotifiedDate);

  const sendReminder = async (user: BasicUserInfo) => {
    setLoading(true);
    try {
      await sendWhereaboutsReminder(user.userId, date);
      // Refresh from the server so lastNotifiedDate reflects the persisted date rather than a
      // frozen client timestamp, allowing the day count to advance and re-enable the button.
      await onReminderSent();
    } catch (e: any) {
      console.error(e)
      dispatch(failureNotification('Failed to send reminder'));
    } finally {
      setLoading(false);
    }
  }

  return <>
    <th className="table__userCell">
      <MobileOnly>
        <BodySmall>{`${user.firstName} ${user.lastName}`}</BodySmall>
      </MobileOnly>
      <BiggerThanTabletOnly>
        <UserAvatarAndName colour={user.emailVerified ? "blue" : "grey"}
                           firstName={user.firstName}
                           lastName={user.lastName}
                           showEditLink={true}
                           userId={user.userId} />
      </BiggerThanTabletOnly>
    </th>
    <td className="table__userCell table__userCell--hideOnMobile" style={{marginLeft: 'auto'}}>
      <TableContents colour={Colours.darkGrey}>
        {!user.emailVerified && t('User has not verified email')}
      </TableContents>
    </td>
    <td className="table__userCell table__userCell--hideOnMobile">
      <TableContents colour={Colours.darkGrey}>
        {daysSinceReminder !== undefined && user.emailVerified && t('reports-page.unknown-whereabouts.reminder-last-sent', {days: daysSinceReminder})}
      </TableContents>
    </td>
    <td className="table__userCell">
      <TableContents>
        {loading ?
          <SimpleSpinner size={16} /> :
          <OutlineButton size={ButtonSize.Medium} className={'unknownWhereabouts__sendButton'}
                         text={"button.send-reminder"}
                         disabled={loading || !user.emailVerified || (daysSinceReminder !== undefined && daysSinceReminder < 2)}
                         click={() => sendReminder(user)} />
        }
      </TableContents>
    </td>
  </>
}

const ReportingContainer = styled.div`
  max-width: 100%;
  background-color: white;
  padding-top: 24px;
  .unknownWhereabouts__sendButton {
    font-size: 12px;
    line-height: 12px;
    padding: 2px 12px;
  }
  th, td {
    padding: 8px 2px;
  }
  @media (${tablet}) {
    padding: 24px;
    th, td {
      padding: 8px 16px;
    }
    .unknownWhereabouts__sendButton {
      font-size: 14px;
      line-height: 14px;
      padding: 4px 16px;
    }
  }
`

const ReportContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  tr {
    justify-content: space-between;
    display: flex;
  }
`
